import * as React from 'react';
import propz from 'propz';
import { parse } from 'query-string';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { getSchoolFromSU, getAllSchoolAdmins } from 'Src/helpers/service/adminSU/schoolAdminsTable';
import { getTournamentWaitingList, getUpcomingTournaments } from 'Src/helpers/service/adminSU/schoolsTable';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';
import { formatTournamentAges } from 'Src/helpers/ageGroups';
import moment from 'moment';
import {
  addSchoolUnionSchoolStudent,
  createSchoolUnionSchoolForm,
  deleteSchoolUnionSchoolStudent,
  deleteSchoolUnionSchoolForm,
  getAllSchoolUnionSchoolForms,
  getSchoolUnionSchoolStudents,
  getSchoolUnionSchoolStudentsCount,
  updateSchoolUnionSchoolStudent,
  updateSchoolUnionSchoolForm
} from 'Src/helpers/service/adminSU/schoolStudentsTable';
import { AGE_GROUPS, AGE_GROUPS_SORTED } from 'Src/consts/school';
import { DEFAULT_LIMIT, DEFAULT_SKIP, LIMIT } from 'Src/consts/table';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import {
  SchoolUnionSchoolStudentForm,
  SchoolUnionSchoolStudentFormHandle
} from 'Src/components/SchoolUnionSchoolStudentForm/SchoolUnionSchoolStudentForm';
import './SchoolManagement.scss';

type SchoolSection = 'overview' | 'people' | 'students' | 'competition' | 'waiting-list' | 'forms';

const TABS: Array<{ key: SchoolSection; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'people', label: 'Admins' },
  { key: 'competition', label: 'Tournament entries' },
  { key: 'waiting-list', label: 'Waiting list' },
  { key: 'forms', label: 'Reg forms' },
  { key: 'students', label: 'Students' }
];

interface Props { user: AppUser; history: any; location: any; match: { params: { section?: SchoolSection } }; }
interface State {
  school?: any;
  admins: any[];
  tournaments: any[];
  waitingListTournaments: any[];
  isWaitingListLoaded: boolean;
  isWaitingListLoading: boolean;
  forms: any[];
  isLoading: boolean;
  error: string;
  adminSearch: string;
  adminAppliedSearch: string;
  selectedAdminIds: string[];
  students: any[];
  studentsTotal: number;
  studentsPage: number;
  studentSearch: string;
  studentAppliedSearch: string;
  areStudentsLoaded: boolean;
  areStudentsLoading: boolean;
  isStudentModalOpen: boolean;
  editingStudent?: any;
  studentDraft?: any;
  similarStudents: any[];
  identicalStudents: any[];
  selectedStudents: any[];
  isStudentSelectionMenuOpen: boolean;
  studentSelectionMenuPosition?: { top: number; left: number };
  areAllStudentsSelecting: boolean;
  isCreateStudentsChain: boolean;
  isDeleteStudentsModalOpen: boolean;
  isStudentsDeleting: boolean;
  studentFormKey: number;
  studentError: string;
  formSearch: string;
  formAppliedSearch: string;
  selectedFormIds: string[];
  isFormModalOpen: boolean;
  editingForm?: any;
  formDraft: { name: string; age: number };
  formError: string;
  isFormSaving: boolean;
  deleteForms: any[];
  deleteFormStudentsCounts: { [formId: string]: number };
  isDeleteFormsChecking: boolean;
}

export class SchoolManagement extends React.Component<Props, State> {
  private studentFormRef = React.createRef<SchoolUnionSchoolStudentFormHandle>();
  private studentBulkSelectRef = React.createRef<HTMLDivElement>();

  state: State = {
    admins: [],
    tournaments: [],
    waitingListTournaments: [],
    isWaitingListLoaded: false,
    isWaitingListLoading: false,
    forms: [],
    isLoading: true,
    error: '',
    adminSearch: '',
    adminAppliedSearch: '',
    selectedAdminIds: [],
    students: [],
    studentsTotal: 0,
    studentsPage: 1,
    studentSearch: '',
    studentAppliedSearch: '',
    areStudentsLoaded: false,
    areStudentsLoading: false,
    isStudentModalOpen: false,
    editingStudent: undefined,
    studentDraft: undefined,
    similarStudents: [],
    identicalStudents: [],
    selectedStudents: [],
    isStudentSelectionMenuOpen: false,
    studentSelectionMenuPosition: undefined,
    areAllStudentsSelecting: false,
    isCreateStudentsChain: false,
    isDeleteStudentsModalOpen: false,
    isStudentsDeleting: false,
    studentFormKey: 0,
    studentError: '',
    formSearch: '',
    formAppliedSearch: '',
    selectedFormIds: [],
    isFormModalOpen: false,
    editingForm: undefined,
    formDraft: { name: '', age: 0 },
    formError: '',
    isFormSaving: false,
    deleteForms: [],
    deleteFormStudentsCounts: {},
    isDeleteFormsChecking: false
  };

  getSchoolId = (): string => String(parse(this.props.location.search).school || '');

  componentDidMount() {
    document.addEventListener('mousedown', this.onStudentBulkSelectOutsideClick);
    const schoolId = this.getSchoolId();
    if (!schoolId) { this.setState({ isLoading: false, error: 'School was not specified.' }); return; }
    Promise.all([
      getSchoolFromSU(this.props.user, schoolId),
      getAllSchoolAdmins(this.props.user, schoolId),
      getUpcomingTournaments(this.props.user, schoolId),
      getAllSchoolUnionSchoolForms(this.props.user, schoolId)
    ])
      .then(([school, admins, tournaments, forms]) => this.setState(
        { school, admins, tournaments, forms, isLoading: false },
        () => {
          if (this.getActiveSection() === 'students') this.loadStudents(1, '');
          if (this.getActiveSection() === 'waiting-list') this.loadTournamentWaitingList();
        }
      ))
      .catch(() => this.setState({ isLoading: false, error: 'School details could not be loaded.' }));
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onStudentBulkSelectOutsideClick);
  }

  componentDidUpdate(prevProps: Props) {
    const previousSection = prevProps.match.params.section || 'overview';
    const activeSection = this.getActiveSection();
    if (previousSection !== activeSection && activeSection === 'students' && !this.state.areStudentsLoaded) {
      this.loadStudents(1, this.state.studentAppliedSearch);
    }
    if (previousSection !== activeSection && activeSection === 'waiting-list' && !this.state.isWaitingListLoaded) {
      this.loadTournamentWaitingList();
    }
  }

  getActiveSection = (): SchoolSection => this.props.match.params.section || 'overview';

  goBack = () => {
    const routeState: any = this.props.location.state || {};
    this.props.history.push({ pathname: routeState.prevPath || '/schools', search: routeState.search || '' });
  };

  openSection = (section: SchoolSection) => {
    this.props.history.push({ pathname: `/schools/manage/${section}`, search: this.props.location.search, state: this.props.location.state });
  };

  loadTournamentWaitingList = (): void => {
    this.setState({ isWaitingListLoading: true });
    getTournamentWaitingList(this.props.user, this.getSchoolId())
      .then(waitingListTournaments => this.setState({
        waitingListTournaments,
        isWaitingListLoaded: true,
        isWaitingListLoading: false
      }))
      .catch(() => this.setState({ isWaitingListLoaded: true, isWaitingListLoading: false }));
  };

  formatValue = (value: any, fallback: string = 'Not set'): string => {
    if (value === undefined || value === null || value === '') return fallback;
    return String(value)
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/(^|\s)\S/g, letter => letter.toUpperCase());
  };

  renderOverview(): React.ReactNode {
    const { school, admins, tournaments } = this.state;
    const postcode = propz.get(school, ['postcode', 'postcode'], 'Not set');
    const region = propz.get(school, ['region'], '') || propz.get(school, ['postcode', 'region'], '');
    const ages = propz.get(school, ['availableAges'], propz.get(school, ['ageGroups'], []));
    const publicSiteStatus = propz.get(school, ['publicSite', 'status'], 'Not configured');
    const details = [
      { label: 'School type', value: this.formatValue(school.kind) },
      { label: 'Education phase', value: this.formatValue(school.eduPhase) },
      { label: 'Available gender', value: this.formatValue(school.availableGender || school.gender) },
      { label: 'Age groups', value: Array.isArray(ages) && ages.length ? formatAgesCompactForSchool(ages, school) : 'Not set' },
      { label: 'Postcode', value: postcode },
      { label: 'Region', value: this.formatValue(region) },
      { label: 'County', value: school.county || 'Not set' },
      { label: 'Domain', value: school.domain || 'Not set' },
      { label: 'Phone', value: school.phone || 'Not set' },
      { label: 'Email', value: school.email || 'Not set' },
      { label: 'Notifications email', value: school.notificationEmail || 'Not set' },
      { label: 'Sports department email', value: school.sportsDepartmentEmail || 'Not set' },
      { label: 'Public website', value: this.formatValue(publicSiteStatus) }
    ];

    return (
      <div className="usmOverview">
        <div className="usmOverview__stats">
          <article><span>School admins</span><strong>{admins.length}</strong><small>with access to this school</small></article>
          <article><span>Upcoming tournaments</span><strong>{tournaments.length}</strong><small>currently visible to the union</small></article>
          <article><span>Public website</span><strong>{this.formatValue(publicSiteStatus)}</strong><small>{school.domain || 'Domain not set'}</small></article>
          <article><span>Registration</span><strong>{school.availableForRegistration ? 'Open' : 'Closed'}</strong><small>self-registration availability</small></article>
        </div>
        <section className="usmOverview__details">
          <header><div><h2>School details</h2><p>Contact, audience and platform configuration.</p></div></header>
          <div className="usmOverview__detailsGrid">
            {details.map(detail => <div key={detail.label}><span>{detail.label}</span><strong>{detail.value}</strong></div>)}
          </div>
        </section>
        {school.address || school.description ? (
          <section className="usmOverview__notes">
            {school.address ? <div><span>Address</span><p>{school.address}</p></div> : null}
            {school.description ? <div><span>Description</span><p>{school.description}</p></div> : null}
          </section>
        ) : null}
      </div>
    );
  }

  getAdminId = (admin: any, index: number = 0): string =>
    String(admin.id || admin._id || admin.userId || admin.email || index);

  getAdminPhone = (admin: any): string =>
    admin.phone || admin.mobilePhone || propz.get(admin, ['profile', 'phone'], '') || (admin.phones || []).join(', ');

  getFilteredAdmins = (): any[] => {
    const query = this.state.adminAppliedSearch.trim().toLowerCase();
    if (!query) return this.state.admins;
    return this.state.admins.filter(admin =>
      [admin.firstName, admin.lastName, admin.email, this.getAdminPhone(admin)].some(value =>
        String(value || '').toLowerCase().includes(query)
      )
    );
  };

  toggleAdmin = (admin: any, index: number): void => {
    const id = this.getAdminId(admin, index);
    this.setState(state => ({
      selectedAdminIds: state.selectedAdminIds.includes(id)
        ? state.selectedAdminIds.filter(selectedId => selectedId !== id)
        : [...state.selectedAdminIds, id]
    }));
  };

  downloadSelectedAdminsCsv = (): void => {
    const selected = new Set(this.state.selectedAdminIds);
    const rows = this.state.admins
      .filter((admin, index) => selected.has(this.getAdminId(admin, index)))
      .map(admin => [admin.firstName, admin.lastName, admin.email, this.getAdminPhone(admin)]);
    if (!rows.length) return;
    const cell = (value: any) => `"${String(value || '').replace(/"/g, '""')}"`;
    const csv = [['First name', 'Last name', 'Email', 'Phone'], ...rows]
      .map(row => row.map(cell).join(','))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${String(propz.get(this.state.school, ['name'], 'school')).replace(/[^a-z0-9]+/gi, '-')}-admins.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  renderAdmins(): React.ReactNode {
    const admins = this.getFilteredAdmins();
    const visibleIds = admins.map((admin, index) => this.getAdminId(admin, index));
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => this.state.selectedAdminIds.includes(id));
    return (
      <article className="usmAdmins">
        <header className="usmAdmins__header">
          <h2>Admins <span>{this.state.admins.length}</span></h2>
          <div className="usmAdmins__tools">
            <label className="usmAdmins__search">
              <input
                value={this.state.adminSearch}
                onChange={event => this.setState({ adminSearch: event.target.value })}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    this.setState({ adminAppliedSearch: this.state.adminSearch });
                  }
                }}
                placeholder="Search by name, email or phone"
              />
              {this.state.adminSearch ? <button type="button" aria-label="Clear search" onClick={() => this.setState({ adminSearch: '', adminAppliedSearch: '' })}>×</button> : null}
            </label>
          </div>
        </header>
        {this.state.selectedAdminIds.length ? (
          <div className="usmAdmins__selection">
            <strong>{this.state.selectedAdminIds.length} selected</strong>
            <button type="button" className="is-download" onClick={this.downloadSelectedAdminsCsv}>⇩ Download selected CSV</button>
            <button type="button" className="is-clear" onClick={() => this.setState({ selectedAdminIds: [] })}>× Clear selection</button>
          </div>
        ) : null}
        <div className="usmAdmins__tableWrap">
          <table>
            <thead><tr>
              <th className="is-select"><input type="checkbox" checked={allVisibleSelected} onChange={() => this.setState(state => ({ selectedAdminIds: allVisibleSelected ? state.selectedAdminIds.filter(id => !visibleIds.includes(id)) : Array.from(new Set([...state.selectedAdminIds, ...visibleIds])) }))} aria-label="Select all visible admins" /></th>
              <th>Name</th><th>Email</th><th>Phone</th>
            </tr></thead>
            <tbody>
              {admins.map((admin, index) => {
                const id = this.getAdminId(admin, index);
                const name = [admin.firstName, admin.lastName].filter(Boolean).join(' ') || '—';
                const initials = [admin.firstName, admin.lastName].filter(Boolean).map(value => String(value).charAt(0)).join('').toUpperCase() || 'A';
                return <tr key={id}>
                  <td className="is-select"><input type="checkbox" checked={this.state.selectedAdminIds.includes(id)} onChange={() => this.toggleAdmin(admin, index)} aria-label={`Select ${name}`} /></td>
                  <td><div className="usmAdmins__person"><span>{initials}</span><strong>{name}</strong></div></td>
                  <td>{admin.email || '—'}</td><td>{this.getAdminPhone(admin) || '—'}</td>
                </tr>;
              })}
              {!admins.length ? <tr><td colSpan={4} className="usmAdmins__empty">{this.state.adminAppliedSearch ? 'No admins match this search.' : 'No admins found.'}</td></tr> : null}
            </tbody>
          </table>
        </div>
      </article>
    );
  }

  getStudentId = (student: any): string => String(student.id || student._id || student.userId || '');

  getStudentSearchWhere = (search: string): any => {
    const value = search.trim();
    if (!value) return {};
    return {
      $or: [
        { firstName: { like: value, options: 'i' } },
        { lastName: { like: value, options: 'i' } }
      ]
    };
  };

  getCountValue = (result: any): number => {
    const rawCount = result && result.count;
    return typeof rawCount === 'object' ? Number(rawCount.count || 0) : Number(rawCount || 0);
  };

  loadStudents = (page: number = this.state.studentsPage, search: string = this.state.studentAppliedSearch): Promise<void> => {
    const where = this.getStudentSearchWhere(search);
    const filter = {
      where,
      limit: LIMIT,
      skip: Math.max(0, page - 1) * LIMIT,
      order: 'lastName ASC'
    };

    this.setState({ areStudentsLoading: true, studentError: '' });
    return Promise.all([
      getSchoolUnionSchoolStudents(this.props.user, this.getSchoolId(), filter),
      getSchoolUnionSchoolStudentsCount(this.props.user, this.getSchoolId(), where)
    ])
      .then(([students, count]) => this.setState({
        students,
        studentsTotal: this.getCountValue(count),
        studentsPage: page,
        studentAppliedSearch: search,
        areStudentsLoaded: true,
        areStudentsLoading: false
      }))
      .catch(() => this.setState({
        areStudentsLoaded: true,
        areStudentsLoading: false,
        studentError: 'Students could not be loaded. Please try again.'
      }));
  };

  applyStudentSearch = (event?: React.FormEvent): void => {
    if (event) event.preventDefault();
    this.loadStudents(1, this.state.studentSearch.trim());
  };

  clearStudentSearch = (): void => {
    this.setState({ studentSearch: '', studentAppliedSearch: '' }, () => this.loadStudents(1, ''));
  };

  openCreateStudent = (): void => this.setState(state => ({
    isStudentModalOpen: true,
    editingStudent: undefined,
    studentDraft: undefined,
    similarStudents: [],
    identicalStudents: [],
    isCreateStudentsChain: false,
    studentError: '',
    studentFormKey: state.studentFormKey + 1
  }));

  openEditStudent = (student: any): void => {
    if (!propz.get(this.state.school, ['studentImportForAdminAllowed'], false)) return;
    this.setState(state => ({
      isStudentModalOpen: true,
      editingStudent: student,
      studentDraft: undefined,
      similarStudents: [],
      identicalStudents: [],
      isCreateStudentsChain: false,
      studentError: '',
      studentFormKey: state.studentFormKey + 1
    }));
  };

  closeStudentModal = (): void => this.setState({
    isStudentModalOpen: false,
    editingStudent: undefined,
    studentDraft: undefined,
    similarStudents: [],
    identicalStudents: [],
    isCreateStudentsChain: false,
    studentError: ''
  });

  normaliseStudentBirthday = (birthday: any): string =>
    birthday && moment(birthday).isValid() ? moment(birthday).format('YYYY-MM-DD') : '';

  findSimilarStudents = (data: any): Promise<{ identicalStudents: any[]; similarStudents: any[] }> => {
    const firstName = String(data.firstName || '').trim();
    const lastName = String(data.lastName || '').trim();
    const birthday = this.normaliseStudentBirthday(data.birthday);
    const baseFilter = {
      limit: DEFAULT_LIMIT,
      skip: DEFAULT_SKIP,
      where: {
        firstName: { like: firstName, options: 'i' },
        lastName: { like: lastName, options: 'i' }
      }
    };
    const requests: Array<Promise<any[]>> = [
      getSchoolUnionSchoolStudents(this.props.user, this.getSchoolId(), baseFilter)
    ];

    if (birthday) {
      const birthdayRange = { $gte: new Date(birthday), $lte: new Date(birthday) };
      requests.push(
        getSchoolUnionSchoolStudents(this.props.user, this.getSchoolId(), {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          where: { lastName: { like: lastName, options: 'i' }, birthday: birthdayRange }
        }),
        getSchoolUnionSchoolStudents(this.props.user, this.getSchoolId(), {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          where: { firstName: { like: firstName, options: 'i' }, birthday: birthdayRange }
        })
      );
    }

    return Promise.all(requests).then(results => {
      const uniqueStudents: any[] = Array.from(new Map<string, any>(
        ([] as any[]).concat(...results).map(student => [this.getStudentId(student), student] as [string, any])
      ).values());
      const normalizedFirstName = firstName.toLowerCase();
      const normalizedLastName = lastName.toLowerCase();
      const isSameFirstName = (student: any) => String(student.firstName || '').trim().toLowerCase() === normalizedFirstName;
      const isSameLastName = (student: any) => String(student.lastName || '').trim().toLowerCase() === normalizedLastName;
      const isSameBirthday = (student: any) => this.normaliseStudentBirthday(student.birthday) === birthday;
      const identicalStudents = uniqueStudents.filter(student =>
        isSameFirstName(student) && isSameLastName(student) && isSameBirthday(student)
      );
      const similarStudents = uniqueStudents.filter(student => {
        if (identicalStudents.some(identical => this.getStudentId(identical) === this.getStudentId(student))) return false;
        if (!birthday) return isSameFirstName(student) && isSameLastName(student);
        return (isSameFirstName(student) && isSameLastName(student)) ||
          (isSameFirstName(student) && isSameBirthday(student)) ||
          (isSameLastName(student) && isSameBirthday(student));
      });
      return { identicalStudents, similarStudents };
    });
  };

  saveStudent = async (
    formData: any,
    isContinue: boolean,
    isSavingSimilarStudentConfirmed: boolean
  ): Promise<boolean> => {
    const data = { ...formData };
    delete data.form;
    if (!data.formId) delete data.formId;
    data.firstName = String(data.firstName || '').trim();
    data.lastName = String(data.lastName || '').trim();
    this.setState({ studentError: '' });

    try {
      if (this.state.editingStudent) {
        await updateSchoolUnionSchoolStudent(
          this.props.user,
          this.getSchoolId(),
          this.getStudentId(this.state.editingStudent),
          data
        );
        await this.loadStudents(this.state.studentsPage, this.state.studentAppliedSearch);
        this.closeStudentModal();
        return true;
      }

      const { identicalStudents, similarStudents } = await this.findSimilarStudents(data);
      const canCreate = (isSavingSimilarStudentConfirmed && identicalStudents.length === 0) ||
        (identicalStudents.length === 0 && similarStudents.length === 0);

      if (!canCreate) {
        this.setState({ identicalStudents, similarStudents, studentDraft: data });
        return false;
      }

      await addSchoolUnionSchoolStudent(this.props.user, this.getSchoolId(), data);
      await this.loadStudents(1, this.state.studentAppliedSearch);
      this.setState({
        isStudentModalOpen: isContinue,
        editingStudent: undefined,
        studentDraft: undefined,
        similarStudents: [],
        identicalStudents: [],
        isCreateStudentsChain: isContinue
      });
      return true;
    } catch (error) {
      this.setState({
        studentError: propz.get(error, ['response', 'data', 'details', 'text'], '') ||
          'The student could not be saved. Please check the details and try again.'
      });
      return false;
    }
  };

  toggleStudentSelection = (student: any): void => {
    const studentId = this.getStudentId(student);
    this.setState(state => ({
      selectedStudents: state.selectedStudents.some(selected => this.getStudentId(selected) === studentId)
        ? state.selectedStudents.filter(selected => this.getStudentId(selected) !== studentId)
        : [...state.selectedStudents, student]
    }));
  };

  onStudentBulkSelectOutsideClick = (event: MouseEvent): void => {
    if (
      this.state.isStudentSelectionMenuOpen &&
      this.studentBulkSelectRef.current &&
      !this.studentBulkSelectRef.current.contains(event.target as Node)
    ) {
      this.setState({ isStudentSelectionMenuOpen: false });
    }
  };

  selectStudentsOnPage = (): void => {
    this.setState(state => ({
      selectedStudents: [
        ...state.selectedStudents,
        ...this.state.students.filter(student =>
          !state.selectedStudents.some(selected => this.getStudentId(selected) === this.getStudentId(student))
        )
      ],
      isStudentSelectionMenuOpen: false
    }));
  };

  selectAllStudents = (): void => {
    if (this.state.studentsTotal === 0 || this.state.areAllStudentsSelecting) return;
    const where = this.getStudentSearchWhere(this.state.studentAppliedSearch);
    const batchCount = Math.max(1, Math.ceil(this.state.studentsTotal / DEFAULT_LIMIT));
    const requests = Array.from({ length: batchCount }, (_, index) =>
      getSchoolUnionSchoolStudents(this.props.user, this.getSchoolId(), {
        where,
        limit: DEFAULT_LIMIT,
        skip: index * DEFAULT_LIMIT,
        order: 'lastName ASC'
      })
    );

    this.setState({ areAllStudentsSelecting: true, isStudentSelectionMenuOpen: false, studentError: '' });
    Promise.all(requests)
      .then(results => this.setState(state => {
        const selectedById = new Map<string, any>(state.selectedStudents.map(student => [this.getStudentId(student), student]));
        ([] as any[]).concat(...results).forEach(student => selectedById.set(this.getStudentId(student), student));
        return { selectedStudents: Array.from(selectedById.values()), areAllStudentsSelecting: false };
      }))
      .catch(() => this.setState({
        areAllStudentsSelecting: false,
        studentError: 'All students could not be selected. Please try again.'
      }));
  };

  clearStudentSelection = (): void => this.setState({
    selectedStudents: [],
    isStudentSelectionMenuOpen: false
  });

  renderStudentBulkSelect(): React.ReactNode {
    const selectedIds = new Set(this.state.selectedStudents.map(this.getStudentId));
    const allPageSelected = this.state.students.length > 0 &&
      this.state.students.every(student => selectedIds.has(this.getStudentId(student)));

    return <div className="usmStudentsBulkSelect" ref={this.studentBulkSelectRef}>
      <button
        type="button"
        className={`usmStudentsBulkSelect__trigger${allPageSelected ? ' is-checked' : ''}`}
        aria-label="Bulk select students"
        aria-expanded={this.state.isStudentSelectionMenuOpen}
        disabled={this.state.areStudentsLoading || this.state.areAllStudentsSelecting}
        onClick={event => {
          event.stopPropagation();
          const rect = event.currentTarget.getBoundingClientRect();
          this.setState(state => ({
            isStudentSelectionMenuOpen: !state.isStudentSelectionMenuOpen,
            studentSelectionMenuPosition: { top: rect.bottom + 8, left: rect.left }
          }));
        }}
      >
        <span />
      </button>
      {this.state.isStudentSelectionMenuOpen ? <div className="usmStudentsBulkSelect__menu" style={this.state.studentSelectionMenuPosition}>
        <button type="button" disabled={this.state.students.length === 0} onClick={this.selectStudentsOnPage}>Select this page</button>
        <button type="button" disabled={this.state.studentsTotal === 0} onClick={this.selectAllStudents}>Select all</button>
        <button type="button" disabled={this.state.selectedStudents.length === 0} onClick={this.clearStudentSelection}>Clear selection</button>
      </div> : null}
    </div>;
  }

  openDeleteStudentsModal = (): void => {
    if (!this.state.selectedStudents.length) return;
    this.setState({ isDeleteStudentsModalOpen: true, studentError: '' });
  };

  closeDeleteStudentsModal = (): void => {
    if (!this.state.isStudentsDeleting) this.setState({ isDeleteStudentsModalOpen: false, studentError: '' });
  };

  deleteSelectedStudents = async (): Promise<void> => {
    const students = this.state.selectedStudents;
    if (!students.length) return;
    this.setState({ isStudentsDeleting: true, studentError: '' });

    const results = await Promise.all(students.map(student =>
      deleteSchoolUnionSchoolStudent(this.props.user, this.getSchoolId(), this.getStudentId(student))
        .then(() => ({ student, isDeleted: true }))
        .catch(() => ({ student, isDeleted: false }))
    ));
    const failedStudents = results.filter(result => !result.isDeleted).map(result => result.student);
    const deletedCount = results.length - failedStudents.length;

    if (deletedCount > 0) {
      const remainingTotal = Math.max(0, this.state.studentsTotal - deletedCount);
      const lastPage = Math.max(1, Math.ceil(remainingTotal / LIMIT));
      await this.loadStudents(Math.min(this.state.studentsPage, lastPage), this.state.studentAppliedSearch);
    }

    if (failedStudents.length > 0) {
      this.setState({
        selectedStudents: failedStudents,
        isStudentsDeleting: false,
        studentError: `${failedStudents.length} ${failedStudents.length === 1 ? 'student could' : 'students could'} not be removed. Please try again.`
      });
      return;
    }

    this.setState({
      selectedStudents: [],
      isDeleteStudentsModalOpen: false,
      isStudentsDeleting: false,
      studentError: ''
    });
  };

  renderDeleteStudentsModal(): React.ReactNode {
    const students = this.state.selectedStudents;
    const schoolName = propz.get(this.state.school, ['name'], 'this school');
    const title = students.length === 1 ? 'Remove student from school?' : 'Remove students from school?';
    return <SimpleModal
      isOpen={this.state.isDeleteStudentsModalOpen}
      onCloseClick={this.closeDeleteStudentsModal}
      title={title}
      showFooter={false}
      isBodyBold={false}
      customClass="usmStudentDeleteModal"
      dialogStyle={{ maxWidth: 500 }}
    >
      <div className="usmStudentDelete">
        <p>The Student role for {schoolName} will be removed from the selected {students.length === 1 ? 'student' : 'students'}.</p>
        <p>Their user accounts and roles at other schools will not be deleted.</p>
        <ul>{students.map(student => <li key={this.getStudentId(student)}><strong>{[student.firstName, student.lastName].filter(Boolean).join(' ')}</strong><span>{propz.get(student, ['form', 'name'], 'No reg form')}</span></li>)}</ul>
        {this.state.studentError ? <div className="usmStudents__error">{this.state.studentError}</div> : null}
        <footer>
          <button type="button" onClick={this.closeDeleteStudentsModal} disabled={this.state.isStudentsDeleting}>Cancel</button>
          <button type="button" className="is-danger" onClick={this.deleteSelectedStudents} disabled={this.state.isStudentsDeleting}>{this.state.isStudentsDeleting ? 'Removing…' : `Remove ${students.length === 1 ? 'student' : `${students.length} students`}`}</button>
        </footer>
      </div>
    </SimpleModal>;
  }

  renderStudentModal(): React.ReactNode {
    const student = this.state.editingStudent || this.state.studentDraft;
    const title = this.state.editingStudent ? 'Edit student' : 'Add student';
    return <SimpleModal
      isOpen={this.state.isStudentModalOpen}
      onCloseClick={() => this.studentFormRef.current?.requestClose()}
      title={title}
      showFooter={false}
      customClass="unionStudentModal usmStudentModal"
      dialogStyle={{ maxWidth: 560 }}
    >
      {this.state.studentError ? <div className="usmStudents__error">{this.state.studentError}</div> : null}
      <SchoolUnionSchoolStudentForm
        key={this.state.studentFormKey}
        ref={this.studentFormRef}
        student={student}
        onSubmit={this.saveStudent}
        onCancel={this.closeStudentModal}
        user={this.props.user}
        schoolId={this.getSchoolId()}
        isCreateStudentsChain={this.state.isCreateStudentsChain}
        identicalStudents={this.state.identicalStudents}
        similarStudents={this.state.similarStudents}
        isFormsExist={this.state.forms.length > 0}
        isEditStudent={Boolean(this.state.editingStudent)}
      />
    </SimpleModal>;
  }

  renderStudents(): React.ReactNode {
    const canManage = Boolean(propz.get(this.state.school, ['studentImportForAdminAllowed'], false));
    const selectedIds = new Set(this.state.selectedStudents.map(this.getStudentId));
    const pageCount = Math.max(1, Math.ceil(this.state.studentsTotal / LIMIT));
    const pageNumbers = Array.from({ length: pageCount }, (_, index) => index + 1).filter(page =>
      page === 1 || page === pageCount || Math.abs(page - this.state.studentsPage) <= 2
    );

    return <article className="usmStudents">
      <header className="usmStudents__header">
        <h2>Students <span>{this.state.studentsTotal}</span></h2>
        <form className="usmStudents__search" onSubmit={this.applyStudentSearch}>
          <input
            value={this.state.studentSearch}
            onChange={event => this.setState({ studentSearch: event.target.value })}
            placeholder="Search by first name or surname"
            aria-label="Search students by first name or surname"
          />
          {this.state.studentSearch ? <button type="button" aria-label="Clear student search" onClick={this.clearStudentSearch}>×</button> : null}
        </form>
        {canManage ? <button type="button" className="usmStudents__add" onClick={this.openCreateStudent}>+ Add student</button> : null}
      </header>
      {!canManage ? <div className="usmStudents__misNotice"><strong>Managed by MIS</strong><span>Students are synchronised by the school’s MIS integration and are read-only in Squad In Touch.</span></div> : null}
      {canManage && (this.state.selectedStudents.length || this.state.areAllStudentsSelecting) ? <div className="usmStudents__selection">
        <strong>{this.state.areAllStudentsSelecting ? 'Selecting all students…' : `${this.state.selectedStudents.length} ${this.state.selectedStudents.length === 1 ? 'student' : 'students'} selected`}</strong>
        <button type="button" className="is-danger" disabled={this.state.areAllStudentsSelecting} onClick={this.openDeleteStudentsModal}>Delete students</button>
        <button type="button" className="is-clear" disabled={this.state.areAllStudentsSelecting} onClick={this.clearStudentSelection}>Clear selection</button>
      </div> : null}
      {this.state.studentError && !this.state.isStudentModalOpen && !this.state.isDeleteStudentsModalOpen ? <div className="usmStudents__error usmStudents__error--list">{this.state.studentError}</div> : null}
      <div className="usmStudents__tableWrap">
        <table>
          <thead><tr>{canManage ? <th className="is-select">{this.renderStudentBulkSelect()}</th> : null}<th>Student</th><th>Reg form</th><th>Year group</th><th>Gender</th><th>Date of birth</th></tr></thead>
          <tbody>
            {this.state.areStudentsLoading ? <tr><td colSpan={canManage ? 6 : 5} className="usmStudents__empty">Loading students…</td></tr> : this.state.students.map(student => {
              const fullName = [student.firstName, student.lastName].filter(Boolean).join(' ') || 'Unnamed student';
              const initials = [student.firstName, student.lastName].filter(Boolean).map(value => String(value).charAt(0)).join('').toUpperCase() || 'S';
              const studentId = this.getStudentId(student);
              return <tr key={studentId} className={canManage ? 'is-editable' : ''} data-selected={selectedIds.has(studentId)} onClick={() => this.openEditStudent(student)}>
                {canManage ? <td className="is-select" onClick={event => event.stopPropagation()}><input type="checkbox" checked={selectedIds.has(studentId)} onChange={() => this.toggleStudentSelection(student)} aria-label={`Select ${fullName}`} /></td> : null}
                <td><div className="usmStudents__person"><span>{initials}</span><strong>{fullName}</strong></div></td>
                <td>{propz.get(student, ['form', 'name'], '—')}</td>
                <td><span className="usmStudents__age">{propz.get(student, ['form', 'ageGroup'], '—')}</span></td>
                <td>{this.formatValue(student.gender, '—')}</td>
                <td>{student.birthday && moment(student.birthday).isValid() ? moment(student.birthday).format('D MMM YYYY') : '—'}</td>
              </tr>;
            })}
            {!this.state.areStudentsLoading && !this.state.students.length ? <tr><td colSpan={canManage ? 6 : 5} className="usmStudents__empty">{this.state.studentAppliedSearch ? 'No students match this search.' : 'No students found.'}</td></tr> : null}
          </tbody>
        </table>
      </div>
      {this.state.studentsTotal > LIMIT ? <div className="usmStudents__pagination">
        <span>Page {this.state.studentsPage} of {pageCount}</span>
        <button type="button" disabled={this.state.studentsPage <= 1 || this.state.areStudentsLoading} onClick={() => this.loadStudents(this.state.studentsPage - 1)}>Previous</button>
        {pageNumbers.map((page, index) => <React.Fragment key={page}>
          {index > 0 && page - pageNumbers[index - 1] > 1 ? <i>…</i> : null}
          <button type="button" className={page === this.state.studentsPage ? 'is-active' : ''} disabled={this.state.areStudentsLoading} onClick={() => this.loadStudents(page)}>{page}</button>
        </React.Fragment>)}
        <button type="button" disabled={this.state.studentsPage >= pageCount || this.state.areStudentsLoading} onClick={() => this.loadStudents(this.state.studentsPage + 1)}>Next</button>
      </div> : null}
      {this.renderStudentModal()}
      {this.renderDeleteStudentsModal()}
    </article>;
  }

  openTournament = (tournament: any): void => {
    this.props.history.push({
      pathname: '/tournaments/manage',
      search: `tournament=${tournament.id}`,
      state: {
        prevPath: '/schools/manage/competition',
        search: this.props.location.search
      }
    });
  };

  renderTournamentEntries(): React.ReactNode {
    const tournaments = this.state.tournaments.slice().sort((left, right) =>
      new Date(left.startTime).getTime() - new Date(right.startTime).getTime()
    );
    const nextTournament = tournaments[0];
    const publishedCount = tournaments.filter(tournament => tournament.isPublishOnTheWebsite).length;
    return (
      <article className="usmTournamentEntries">
        <header className="usmTournamentEntries__header">
          <div>
            <h2>Upcoming union tournaments <span>{tournaments.length}</span></h2>
            <p>Tournaments organised by this union where the school has a confirmed entry.</p>
          </div>
          {nextTournament ? <div className="usmTournamentEntries__summary"><span>Next start</span><strong>{moment(nextTournament.startTime).format('D MMM YYYY')}</strong></div> : null}
          {tournaments.length ? <div className="usmTournamentEntries__summary"><span>Published</span><strong>{publishedCount} of {tournaments.length}</strong></div> : null}
        </header>
        <div className="usmTournamentEntries__tableWrap">
          <table>
            <thead><tr><th>Tournament</th><th>Audience</th><th>Dates</th><th>Venue</th><th>Entry deadline</th><th aria-label="Open" /></tr></thead>
            <tbody>
              {tournaments.map(tournament => {
                const type = this.formatValue(tournament.type, 'Tournament');
                const audience = [formatTournamentAges(tournament.ages || []), this.formatValue(tournament.gender, '')].filter(Boolean).join(' · ') || 'Not set';
                const venue = propz.get(tournament, ['venue', 'name'], '') || propz.get(tournament, ['venue', 'postcode'], '') || 'Not set';
                const start = moment(tournament.startTime);
                const end = moment(tournament.endTime);
                const dateLabel = start.isSame(end, 'day') ? start.format('D MMM YYYY') : `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
                return <tr key={tournament.id} onClick={() => this.openTournament(tournament)}>
                  <td><div className="usmTournamentEntries__name"><i /><div><strong>{tournament.name}</strong><span>{tournament.sportName || 'Sport not set'} · {type}</span></div></div></td>
                  <td><span className="usmTournamentEntries__audience">{audience}</span></td>
                  <td><strong className="usmTournamentEntries__date">{dateLabel}</strong></td>
                  <td>{venue}</td>
                  <td>{tournament.deadlineForEntries ? moment(tournament.deadlineForEntries).format('D MMM YYYY') : 'Not set'}</td>
                  <td className="usmTournamentEntries__arrow">›</td>
                </tr>;
              })}
              {!tournaments.length ? <tr><td colSpan={6} className="usmTournamentEntries__empty">This school has no upcoming entries in tournaments organised by the union.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </article>
    );
  }

  renderTournamentWaitingList(): React.ReactNode {
    if (this.state.isWaitingListLoading) return <Loader />;

    const tournaments = this.state.waitingListTournaments;
    return (
      <article className="usmTournamentEntries">
        <header className="usmTournamentEntries__header">
          <div>
            <h2>Tournament waiting list <span>{tournaments.length}</span></h2>
            <p>Upcoming union tournaments where this school is waiting for a confirmed place.</p>
          </div>
        </header>
        <div className="usmTournamentEntries__tableWrap">
          <table>
            <thead><tr><th>Tournament</th><th>Position</th><th>Audience</th><th>Dates</th><th>Joined</th><th aria-label="Open" /></tr></thead>
            <tbody>
              {tournaments.map(tournament => {
                const audience = [formatTournamentAges(tournament.ages || []), this.formatValue(tournament.gender, '')].filter(Boolean).join(' · ') || 'Not set';
                const start = moment(tournament.startTime);
                const end = moment(tournament.endTime);
                const dateLabel = start.isSame(end, 'day') ? start.format('D MMM YYYY') : `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
                return <tr key={tournament.id} onClick={() => this.openTournament(tournament)}>
                  <td><div className="usmTournamentEntries__name"><i /><div><strong>{tournament.name}</strong><span>{this.formatValue(tournament.type, 'Tournament')}</span></div></div></td>
                  <td><strong className="usmTournamentEntries__date">#{tournament.waitingListPosition}</strong></td>
                  <td><span className="usmTournamentEntries__audience">{audience}</span></td>
                  <td><strong className="usmTournamentEntries__date">{dateLabel}</strong></td>
                  <td>{tournament.waitingListJoinedAt ? moment(tournament.waitingListJoinedAt).format('D MMM YYYY') : 'Not set'}</td>
                  <td className="usmTournamentEntries__arrow">›</td>
                </tr>;
              })}
              {!tournaments.length ? <tr><td colSpan={6} className="usmTournamentEntries__empty">This school is not currently on a tournament waiting list.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </article>
    );
  }

  getFormAgeOptions = (): string[] => {
    const naming = propz.get(this.state.school, ['ageGroupsNaming'], 'ENGLISH');
    return propz.get(AGE_GROUPS, [naming], AGE_GROUPS.ENGLISH);
  };

  getFormAgeSections = (): Array<{ label: string; ages: Array<{ label: string; value: number }> }> => {
    const naming = propz.get(this.state.school, ['ageGroupsNaming'], 'ENGLISH');
    const ageOptions = this.getFormAgeOptions();
    const orderedAgeOptions = propz.get(AGE_GROUPS_SORTED, [naming], ageOptions);
    const ages = orderedAgeOptions
      .map(label => ({ label, value: ageOptions.indexOf(label) }))
      .filter(age => age.value !== -1);

    if (naming !== 'ENGLISH') return [{ label: 'Age groups', ages }];

    const definitions = [
      { label: 'Early years', values: ['N1', 'N2', 'Reception'] },
      { label: 'KS1', values: ['Y1', 'Y2'] },
      { label: 'KS2', values: ['Y3', 'Y4', 'Y5', 'Y6'] },
      { label: 'KS3', values: ['Y7', 'Y8', 'Y9'] },
      { label: 'KS4', values: ['Y10', 'Y11'] },
      { label: 'KS5', values: ['Y12', 'Y13'] },
      { label: 'Further education', values: ['Y14', 'Post-19'] }
    ];

    return definitions
      .map(section => ({
        label: section.label,
        ages: ages.filter(age => section.values.includes(age.label))
      }))
      .filter(section => section.ages.length > 0);
  };

  renderFormAgeChips = (): React.ReactNode => (
    <div className="usmRegFormAgeGroups" role="radiogroup" aria-label="Year group">
      {this.getFormAgeSections().map(section => (
        <section className="usmRegFormAgeGroups__section" key={section.label}>
          <strong>{section.label}</strong>
          <div className="usmRegFormAgeGroups__chips">
            {section.ages.map(age => (
              <button
                type="button"
                role="radio"
                aria-checked={this.state.formDraft.age === age.value}
                className={this.state.formDraft.age === age.value ? 'is-selected' : ''}
                key={age.label}
                onClick={() => this.setState({
                  formDraft: { ...this.state.formDraft, age: age.value },
                  formError: ''
                })}
              >
                {age.label}
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );

  getFormAgeLabel = (form: any): string =>
    form.ageGroup || this.getFormAgeOptions()[Number(form.age)] || 'Not set';

  getFormId = (form: any): string => String(form.id || form._id || '');

  reloadForms = (): Promise<any> =>
    getAllSchoolUnionSchoolForms(this.props.user, this.getSchoolId()).then(forms => this.setState({ forms }));

  openCreateForm = (): void => {
    const schoolAges = propz.get(this.state.school, ['availableAges'], []);
    this.setState({
      isFormModalOpen: true,
      editingForm: undefined,
      formDraft: { name: '', age: schoolAges.length ? Number(schoolAges[0]) : 0 },
      formError: ''
    });
  };

  openEditForm = (form: any): void => this.setState({
    isFormModalOpen: false,
    editingForm: form,
    formDraft: { name: form.name || '', age: Number(form.age) },
    formError: ''
  });

  closeFormDrawer = (): void => {
    if (!this.state.isFormSaving) this.setState({ editingForm: undefined, formError: '' });
  };

  closeFormModal = (): void => {
    if (!this.state.isFormSaving) this.setState({ isFormModalOpen: false, editingForm: undefined, formError: '' });
  };

  saveForm = (event: React.FormEvent): void => {
    event.preventDefault();
    const name = this.state.formDraft.name.trim();
    if (!name) { this.setState({ formError: 'Form name is required.' }); return; }
    const editingFormId = this.state.editingForm ? this.getFormId(this.state.editingForm) : '';
    const duplicate = this.state.forms.some(form =>
      this.getFormId(form) !== editingFormId &&
      Number(form.age) === Number(this.state.formDraft.age) &&
      String(form.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      this.setState({ formError: 'A registration form with this name already exists for the selected year group.' });
      return;
    }
    const request = this.state.editingForm
      ? updateSchoolUnionSchoolForm(this.props.user, this.getSchoolId(), editingFormId, { name, age: this.state.formDraft.age })
      : createSchoolUnionSchoolForm(this.props.user, this.getSchoolId(), { name, age: this.state.formDraft.age });
    this.setState({ isFormSaving: true, formError: '' });
    request
      .then(() => this.reloadForms())
      .then(() => this.setState({ isFormSaving: false, isFormModalOpen: false, editingForm: undefined }))
      .catch(error => this.setState({ isFormSaving: false, formError: propz.get(error, ['response', 'data', 'details', 'text'], '') || 'The form could not be saved.' }));
  };

  toggleFormSelection = (form: any): void => {
    const id = this.getFormId(form);
    this.setState(state => ({
      selectedFormIds: state.selectedFormIds.includes(id)
        ? state.selectedFormIds.filter(selectedId => selectedId !== id)
        : [...state.selectedFormIds, id]
    }));
  };

  setVisibleFormsSelected = (forms: any[], isSelected: boolean): void => {
    const visibleIds = forms.map(this.getFormId);
    this.setState(state => ({
      selectedFormIds: isSelected
        ? Array.from(new Set([...state.selectedFormIds, ...visibleIds]))
        : state.selectedFormIds.filter(id => !visibleIds.includes(id))
    }));
  };

  openDeleteForms = (): void => {
    const selectedIds = new Set(this.state.selectedFormIds);
    const deleteForms = this.state.forms.filter(form => selectedIds.has(this.getFormId(form)));
    if (!deleteForms.length) return;

    this.setState({
      deleteForms,
      deleteFormStudentsCounts: {},
      isDeleteFormsChecking: true,
      formError: ''
    });

    Promise.all(deleteForms.map(form =>
      getSchoolUnionSchoolStudentsCount(this.props.user, this.getSchoolId(), { formId: this.getFormId(form) })
        .then((result: any) => {
          const rawCount = result && result.count;
          return typeof rawCount === 'object' ? Number(rawCount.count || 0) : Number(rawCount || 0);
        })
        .catch(() => -1)
    )).then(counts => {
      const deleteFormStudentsCounts = deleteForms.reduce((result, form, index) => ({
        ...result,
        [this.getFormId(form)]: counts[index]
      }), {});
      this.setState({ deleteFormStudentsCounts, isDeleteFormsChecking: false });
    });
  };

  closeDeleteForms = (): void => {
    if (!this.state.isFormSaving) {
      this.setState({ deleteForms: [], deleteFormStudentsCounts: {}, isDeleteFormsChecking: false, formError: '' });
    }
  };

  confirmDeleteForms = (): void => {
    const deletableForms = this.state.deleteForms.filter(form =>
      this.state.deleteFormStudentsCounts[this.getFormId(form)] === 0
    );
    if (!deletableForms.length) return;

    const deletedIds = deletableForms.map(this.getFormId);
    this.setState({ isFormSaving: true, formError: '' });
    Promise.all(deletableForms.map(form =>
      deleteSchoolUnionSchoolForm(this.props.user, this.getSchoolId(), this.getFormId(form))
    ))
      .then(() => this.reloadForms())
      .then(() => this.setState(state => ({
        isFormSaving: false,
        deleteForms: [],
        deleteFormStudentsCounts: {},
        selectedFormIds: state.selectedFormIds.filter(id => !deletedIds.includes(id)),
        editingForm: state.editingForm && deletedIds.includes(this.getFormId(state.editingForm))
          ? undefined
          : state.editingForm
      })))
      .catch(() => this.setState({ isFormSaving: false, formError: 'The selected forms could not be deleted.' }));
  };

  renderRegForms(): React.ReactNode {
    const query = this.state.formAppliedSearch.trim().toLowerCase();
    const forms = this.state.forms
      .filter(form => !query || [form.name, this.getFormAgeLabel(form)].some(value => String(value || '').toLowerCase().includes(query)))
      .sort((left, right) => Number(left.age) - Number(right.age) || String(left.name).localeCompare(String(right.name)));
    const canManage = Boolean(propz.get(this.state.school, ['studentImportForAdminAllowed'], false));
    const selectedIds = new Set(this.state.selectedFormIds);
    const allVisibleSelected = forms.length > 0 && forms.every(form => selectedIds.has(this.getFormId(form)));
    return (
      <article className="usmRegForms">
        <header className="usmRegForms__header">
          <h2>Reg forms <span>{this.state.forms.length}</span></h2>
          <label className="usmAdmins__search usmRegForms__search">
            <input
              value={this.state.formSearch}
              onChange={event => this.setState({ formSearch: event.target.value })}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  this.setState({ formAppliedSearch: this.state.formSearch });
                }
              }}
              placeholder="Search by form name or age group"
            />
            {this.state.formSearch ? <button type="button" aria-label="Clear search" onClick={() => this.setState({ formSearch: '', formAppliedSearch: '' })}>×</button> : null}
          </label>
          {canManage ? <button type="button" className="usmRegForms__add" onClick={this.openCreateForm}>+ Add form</button> : null}
        </header>
        {!canManage ? <div className="usmRegForms__misNotice"><strong>Managed by MIS</strong><span>Registration forms are synchronised by the school’s MIS integration and are read-only in Squad In Touch.</span></div> : null}
        {canManage && this.state.selectedFormIds.length ? (
          <div className="usmRegForms__selection">
            <strong>{this.state.selectedFormIds.length} {this.state.selectedFormIds.length === 1 ? 'form' : 'forms'} selected</strong>
            <button type="button" className="is-danger" onClick={this.openDeleteForms}>Delete</button>
            <button type="button" className="is-clear" onClick={() => this.setState({ selectedFormIds: [] })}>× Clear selection</button>
          </div>
        ) : null}
        <div className="usmRegForms__tableWrap">
          <table><thead><tr>
            {canManage ? <th className="is-select"><input type="checkbox" checked={allVisibleSelected} onChange={() => this.setVisibleFormsSelected(forms, !allVisibleSelected)} aria-label="Select all visible registration forms" /></th> : null}
            <th>Form name</th><th>Age group</th><th>Students</th><th>Updated</th>
          </tr></thead>
            <tbody>
              {forms.map(form => {
                const id = this.getFormId(form);
                return <tr key={id} data-selected={selectedIds.has(id)} onClick={() => this.openEditForm(form)}>
                {canManage ? <td className="is-select" onClick={event => event.stopPropagation()}><input type="checkbox" checked={selectedIds.has(id)} onChange={() => this.toggleFormSelection(form)} aria-label={`Select ${form.name || 'registration form'}`} /></td> : null}
                <td><div className="usmRegForms__name"><span>{String(form.name || 'F').charAt(0).toUpperCase()}</span><strong>{form.name || 'Unnamed form'}</strong></div></td>
                <td><span className="usmRegForms__age">{this.getFormAgeLabel(form)}</span></td>
                <td>{typeof form.studentsCount === 'number' ? form.studentsCount : form.isContainStudents ? 'Has students' : '—'}</td>
                <td>{form.updatedAt || form.createdAt ? moment(form.updatedAt || form.createdAt).format('D MMM YYYY') : '—'}</td>
              </tr>;
              })}
              {!forms.length ? <tr><td colSpan={canManage ? 5 : 4} className="usmRegForms__empty">{query ? 'No registration forms match this search.' : 'No registration forms have been added yet.'}</td></tr> : null}
            </tbody>
          </table>
        </div>
        {this.renderRegFormDrawer()}
        {this.renderRegFormModal()}
        {this.renderDeleteRegFormModal()}
      </article>
    );
  }

  renderRegFormDrawer(): React.ReactNode {
    const form = this.state.editingForm;
    if (!form || this.state.isFormModalOpen) return null;

    const canManage = Boolean(propz.get(this.state.school, ['studentImportForAdminAllowed'], false));
    const studentsCount = typeof form.studentsCount === 'number'
      ? form.studentsCount
      : form.isContainStudents
      ? 'Linked'
      : '—';
    const updatedAt = form.updatedAt || form.createdAt;

    return <>
      <div className="usmRegFormDrawer__scrim" onClick={this.closeFormDrawer} />
      <aside className="usmRegFormDrawer" role="dialog" aria-modal="true" aria-label={`${form.name || 'Registration form'} details`}>
        <header className="usmRegFormDrawer__header">
          <span className="usmRegFormDrawer__icon" aria-hidden="true">{String(form.name || 'R').charAt(0).toUpperCase()}</span>
          <div><small>Registration form</small><h2>{form.name || 'Unnamed form'}</h2><p>{propz.get(this.state.school, ['name'], 'School')}</p></div>
          <button type="button" aria-label="Close registration form" onClick={this.closeFormDrawer}>×</button>
        </header>
        <div className="usmRegFormDrawer__body">
          <div className="usmRegFormDrawer__stats">
            <div><span>Students</span><strong>{studentsCount}</strong></div>
            <div><span>Age group</span><strong>{this.getFormAgeLabel(form)}</strong></div>
            <div><span>Updated</span><strong>{updatedAt ? moment(updatedAt).format('D MMM YYYY') : '—'}</strong></div>
          </div>
          {canManage ? (
            <form className="usmRegFormDrawer__editor" onSubmit={this.saveForm}>
              <div className="usmRegFormDrawer__sectionTitle">Registration form details</div>
              <label><span>Form name <em>*</em></span><input value={this.state.formDraft.name} onChange={event => this.setState({ formDraft: { ...this.state.formDraft, name: event.target.value }, formError: '' })} /></label>
              <label><span>Year group <em>*</em></span>{this.renderFormAgeChips()}</label>
              {this.state.formError ? <div className="usmRegFormEditor__error">{this.state.formError}</div> : null}
              <footer><button type="button" onClick={this.closeFormDrawer} disabled={this.state.isFormSaving}>Cancel</button><button type="submit" className="is-primary" disabled={this.state.isFormSaving || !this.state.formDraft.name.trim()}>{this.state.isFormSaving ? 'Saving…' : 'Save changes'}</button></footer>
            </form>
          ) : (
            <div className="usmRegFormDrawer__readOnly"><strong>Managed by MIS</strong><p>This registration form is synchronised by the school’s MIS integration and cannot be edited here.</p></div>
          )}
        </div>
      </aside>
    </>;
  }

  renderRegFormModal(): React.ReactNode {
    const title = this.state.editingForm ? 'Edit registration form' : 'Add registration form';
    return <SimpleModal
      isOpen={this.state.isFormModalOpen}
      title={<div className="usmRegFormModal__title"><span aria-hidden="true">R</span><div><strong>{title}</strong><small>School registration form</small></div></div>}
      customClass="usmRegFormModal"
      bodyClassName="usmRegFormModal__body"
      dialogStyle={{ width: 'calc(100vw - 32px)', maxWidth: 560, height: 'auto' }}
      showFooter={false}
      isBodyBold={false}
      onCloseClick={this.closeFormModal}
    >
      <form className="usmRegFormEditor" onSubmit={this.saveForm}>
        <p>Use a clear name that school staff and families will recognise.</p>
        <label><span>Form name <em>*</em></span><input autoFocus value={this.state.formDraft.name} onChange={event => this.setState({ formDraft: { ...this.state.formDraft, name: event.target.value }, formError: '' })} placeholder="For example, 7A" /></label>
        <label><span>Year group <em>*</em></span>{this.renderFormAgeChips()}</label>
        {this.state.formError ? <div className="usmRegFormEditor__error">{this.state.formError}</div> : null}
        <footer><button type="button" onClick={this.closeFormModal} disabled={this.state.isFormSaving}>Cancel</button><button type="submit" className="is-primary" disabled={this.state.isFormSaving || !this.state.formDraft.name.trim()}>{this.state.isFormSaving ? 'Saving…' : this.state.editingForm ? 'Save changes' : 'Add form'}</button></footer>
      </form>
    </SimpleModal>;
  }

  renderDeleteRegFormModal(): React.ReactNode {
    const { deleteForms, deleteFormStudentsCounts, isDeleteFormsChecking } = this.state;
    const deletableForms = deleteForms.filter(form => deleteFormStudentsCounts[this.getFormId(form)] === 0);
    const blockedForms = deleteForms.filter(form => (deleteFormStudentsCounts[this.getFormId(form)] || 0) > 0);
    const failedChecks = deleteForms.filter(form => deleteFormStudentsCounts[this.getFormId(form)] === -1);
    const title = deleteForms.length === 1 ? 'Delete registration form?' : 'Delete registration forms?';
    return <SimpleModal isOpen={deleteForms.length > 0} title={title} customClass="usmRegFormModal usmRegFormDeleteModal" bodyClassName="usmRegFormModal__body" dialogStyle={{ width: 'calc(100vw - 32px)', maxWidth: 500, height: 'auto' }} showFooter={false} isBodyBold={false} onCloseClick={this.closeDeleteForms}>
      <div className="usmRegFormDelete">
        {isDeleteFormsChecking ? <p>Checking the selected forms for linked students…</p> : <>
          {deletableForms.length ? <><strong>{deletableForms.length} {deletableForms.length === 1 ? 'form' : 'forms'} will be permanently removed.</strong><ul>{deletableForms.map(form => <li key={this.getFormId(form)}>{form.name}</li>)}</ul><p>This action cannot be undone.</p></> : null}
          {blockedForms.length ? <div className="usmRegFormDelete__blocked"><strong>{blockedForms.length} {blockedForms.length === 1 ? 'form cannot' : 'forms cannot'} be deleted</strong><p>Move linked students to another registration form first.</p><ul>{blockedForms.map(form => { const count = deleteFormStudentsCounts[this.getFormId(form)]; return <li key={this.getFormId(form)}>{form.name} — {count} {count === 1 ? 'student' : 'students'}</li>; })}</ul></div> : null}
          {failedChecks.length ? <div className="usmRegFormDelete__blocked"><strong>Some forms could not be checked</strong><ul>{failedChecks.map(form => <li key={this.getFormId(form)}>{form.name}</li>)}</ul></div> : null}
        </>}
        {this.state.formError ? <div className="usmRegFormEditor__error">{this.state.formError}</div> : null}
        <footer><button type="button" onClick={this.closeDeleteForms} disabled={this.state.isFormSaving}>Cancel</button>{!isDeleteFormsChecking && deletableForms.length ? <button type="button" className="is-danger" onClick={this.confirmDeleteForms} disabled={this.state.isFormSaving}>{this.state.isFormSaving ? 'Deleting…' : `Delete ${deletableForms.length === 1 ? 'form' : `${deletableForms.length} forms`}`}</button> : null}</footer>
      </div>
    </SimpleModal>;
  }

  renderSection(): React.ReactNode {
    switch (this.getActiveSection()) {
      case 'people':
        return this.renderAdmins();
      case 'students':
        return this.renderStudents();
      case 'competition':
        return this.renderTournamentEntries();
      case 'waiting-list':
        return this.renderTournamentWaitingList();
      case 'forms':
        return this.renderRegForms();
      default:
        return this.renderOverview();
    }
  }

  render() {
    const { school, isLoading, error } = this.state;
    if (isLoading) return <Loader />;
    if (error || !school) return <div className="usmError"><h1>School unavailable</h1><p>{error}</p><button onClick={this.goBack}>Back to schools</button></div>;
    const name = propz.get(school, ['name'], 'School');
    const pic = propz.get(school, ['pic'], '');
    const postcode = propz.get(school, ['postcode', 'postcode'], 'Postcode not set');
    const domain = propz.get(school, ['domain'], 'Domain not set');
    const initials = name.split(/\s+/).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase();
    return (
      <div className="usmPage">
        <button type="button" className="usmBack" onClick={this.goBack}>← Schools</button>
        <header className="usmHero">
          <span className="usmLogo"><b>{initials || 'S'}</b>{pic ? <img src={pic} alt="" /> : null}</span>
          <div className="usmIdentity"><span>Member school</span><h1>{name}</h1><div><em>{postcode}</em><em>{domain}</em>{school.email ? <em>{school.email}</em> : null}</div></div>
        </header>
        <nav className="usmTabs" aria-label="School management sections">
          {TABS.map(tab => <button key={tab.key} className={this.getActiveSection() === tab.key ? 'is-active' : ''} onClick={() => this.openSection(tab.key)}>{tab.label}</button>)}
        </nav>
        <main className="usmContent">{this.renderSection()}</main>
      </div>
    );
  }
}
